import React from 'react'
import Head from 'next/head'
import clsx from 'clsx'

import { Text } from '@wartek-id/text-v1'

import { IS_ASSESSMENT_REVAMP_ENABLED } from 'configs/toggle'
import { useAPIGetHomeBanners } from 'api/home-banners/useAPIHomeBanners'
import BottomNav from 'app/RuangGTKHomepageV3/components/BottomNav'
import Header from 'app/RuangGTKHomepageV3/components/Header'
import Carousel from 'app/RuangGTKHomepageV3/components/Carousel'
import KSCard from 'app/RuangGTKHomepageV3/components/KSCard'
import styles from './styles.module.css'
import MenuPanel from 'app/RuangGTKHomepageV3/components/MenuPanel'
import { useMediaQueryHelper } from 'app/RuangGTKHomepageV3/hooks/useMediaQueryHelper'
import { useCheckShowMenu } from 'app/RuangGTKHomepageV3/hooks/useCheckShowMenu'
import { TRACKER_REFKOM_QUERY_SOURCE_KEY } from 'app/PemetaanKompetensiPage/hooks/useSETrackEventRefkom'

import type { MenuPanelProps } from 'app/RuangGTKHomepageV3/components/MenuPanel'

const Homepage = () => {
  const {
    showSertifikasiPendidikMenu,
    showPengelolaanKinerjaMenu,
    showRefleksiKompetensiMenu,
  } = useCheckShowMenu()
  const { data } = useAPIGetHomeBanners()
  const { isDesktop } = useMediaQueryHelper()
  const banners = data?.data

  const MENU_PANEL_ITEMS: MenuPanelProps[] = [
    {
      title: 'Belajar Berkelanjutan',
      items: [
        {
          title: 'Diklat',
          href: process.env.NEXT_PUBLIC_LMS_DIKLAT_URL + '?source=rgtk',
          iconUrl: '/icon/ruang-gtk/pr-lms-v1.svg',
          subtitle: 'Pelatihan terbimbing menggunakan LMS',
          tracker: { activityType: 'CLICK_MENU_DIKLAT', keepAlive: true },
        },
        {
          title: 'Sertifikasi Pendidik',
          href: '/sertifikasi-pendidik',
          iconUrl: '/icon/ruang-gtk/pr-sertifikasi-pendidik.svg',
          subtitle: 'Pendidikan Profesi Guru (PPG) bagi Guru tertentu',
          hide: !showSertifikasiPendidikMenu,
          tracker: {
            activityType: 'CLICK_MENU_SERTIFIKASI_PENDIDIK',
            keepAlive: true,
          },
        },
        {
          title: 'Pelatihan Mandiri',
          href: '/pelatihan-mandiri',
          iconUrl: '/icon/ruang-gtk/pr-pelatihan-mandiri-v1.svg',
          subtitle: 'Materi untuk pengembangan kompetensi guru dan tendik',
          tracker: {
            activityType: 'CLICK_MENU_PELATIHAN_MANDIRI',
            keepAlive: true,
          },
        },
        {
          title: 'Komunitas',
          href: '/komunitas?source=HOMEPAGE',
          iconUrl: '/icon/ruang-gtk/pr-komunitas.svg',
          subtitle: '144.000+ komunitas guru & tendik berbagi praktik baik',
          tracker: { activityType: 'CLICK_MENU_KOMUNITAS', keepAlive: true },
        },
      ],
    },
    {
      title: 'Karir dan Kinerja',
      items: [
        {
          hide: !showPengelolaanKinerjaMenu,
          href: '/pengelolaan-kinerja',
          iconUrl: '/icon/ruang-gtk/pr-pengelolaan-kinerja-v1.svg',
          subtitle:
            'Perencanaan, pelaksanaan, dan penilaian kinerja Anda untuk pengembangan diri dan satdik',
          title: 'Pengelolaan Kinerja',
          tracker: { activityType: 'CLICK_MENU_PENGELOLAAN_KINERJA' },
        },
        {
          hide: !showRefleksiKompetensiMenu,
          href: `/refleksi-kompetensi?${TRACKER_REFKOM_QUERY_SOURCE_KEY}=pmm_home`,
          iconUrl: '/icon/ruang-gtk/pr-refleksi-kompetensi.svg',
          subtitle: 'Rekomendasi pembelajaran yang disusun personal untuk Anda',
          title: 'Refleksi Kompetensi',
          tracker: { activityType: 'CLICK_MENU_REFLEKSI_KOMPETENSI' },
        },
        {
          title: 'Seleksi Kepala Sekolah',
          subtitle:
            'Info persyaratan, tahapan, & cek kualifikasi untuk seleksi',
          href: '/bcks/selection',
          iconUrl: '/icon/ruang-gtk/pr-seleksi-kepala-sekolah-v1.svg',
          tracker: { activityType: 'CLICK_MENU_SELEKSI_KEPALA_SEKOLAH' },
        },
      ],
    },
    {
      title: 'Inspirasi Pembelajaran',
      items: [
        {
          title: 'Perangkat Ajar',
          subtitle:
            'Inspirasi perencanaan pembelajaran intrakurikuler & kokurikuler, bahan ajar, dan sejenisnya',
          href: '/perangkat-ajar',
          iconUrl: '/icon/ruang-gtk/pr-perangkat-ajar.svg',
          tracker: {
            activityType: 'CLICK_MENU_PERANGKAT_AJAR',
            keepAlive: true,
          },
        },
        {
          title: 'Video Inspirasi',
          subtitle: 'Kumpulan video terpilih berisi penerapan praktik baik',
          href: '/video-inspirasi',
          iconUrl: '/icon/ruang-gtk/pr-video-inspirasi.svg',
          tracker: {
            activityType: 'CLICK_MENU_VIDEO_INSPIRASI',
            keepAlive: true,
          },
        },
        {
          title: 'CP/ATP',
          subtitle:
            'Capaian Pembelajaran dan Alur Tujuan Pembelajaran sesuai mata pelajaran Anda',
          href: '/kurikulum/referensi-penerapan/capaian-pembelajaran/',
          iconUrl: '/icon/ruang-gtk/pr-cp-atp.svg',
          tracker: { activityType: 'CLICK_MENU_CP_ATP', keepAlive: true },
        },
        {
          title: 'Asesmen (Asesmen Murid & AKM Kelas)',
          subtitle:
            'Ragam asesmen untuk memetakan dan memantau perkembangan belajar',
          href: IS_ASSESSMENT_REVAMP_ENABLED
            ? '/assessment'
            : '/assessment/litnum',
          iconUrl: '/icon/ruang-gtk/pr-asesmen-v1.svg',
          tracker: { activityType: 'CLICK_MENU_ASESMEN' },
        },
        {
          title: 'Ide Praktik',
          subtitle:
            'Praktik baik terpilih untuk menghadirkan pembelajaran yang bermakna, berkesadaran, dan menggembirakan',
          href: '/ide-praktik',
          iconUrl: '/icon/ruang-gtk/pr-ide-praktik.svg',
          tracker: { activityType: 'CLICK_MENU_IDE_PRAKTIK' },
        },
        {
          title: 'Kelas',
          subtitle:
            'Informasi atau data kelompok murid berdasarkan hasil asesmen',
          href: '/learning-group',
          iconUrl: '/icon/ruang-gtk/pr-kelas.svg',
          tracker: { activityType: 'CLICK_MENU_KELAS' },
        },
        {
          title: 'Bukti Karya',
          subtitle: 'Rekam jejak karya, kinerja, dan kompetensi pendidik',
          href: `/bukti-karya?from=HOMEPAGE`,
          iconUrl: '/icon/ruang-gtk/pr-bukti-karya.svg',
          tracker: { activityType: 'CLICK_MENU_BUKTI_KARYA', keepAlive: true },
        },
      ],
    },
    {
      title: 'Dokumen Rujukan',
      items: [
        {
          title: 'Dokumen Rujukan Pengelolaan Kinerja',
          href: '/dokumen/kategori/Y7G6ArDWqJ',
          iconUrl: '/icon/ruang-gtk/pr-dokumen-rujukan-1.svg',
          tracker: { activityType: 'CLICK_DOKUMEN_PENGELOLAAN_KINERJA' },
        },
        {
          title: 'Dokumen Rujukan Pengelolaan Pengelolaan Satuan Pendidikan',
          href: '/dokumen/kategori/o450Zr0mMG',
          iconUrl: '/icon/ruang-gtk/pr-dokumen-rujukan-1.svg',
          tracker: {
            activityType: 'CLICK_DOKUMEN_PENGELOLAAN_SATUAN_PENDIDIKAN',
          },
        },
        {
          title: 'Dokumen Rujukan Pengelolaan Pembelajaran',
          href: '/dokumen/kategori/oWx0b8DZ1a',
          iconUrl: '/icon/ruang-gtk/pr-dokumen-rujukan-1.svg',
          tracker: { activityType: 'CLICK_DOKUMEN_PENGELOLAAN_PEMBELAJARAN' },
        },
        {
          title: 'Dokumen Rujukan Pengelolaan Peningkatan Kompetensi',
          href: '/dokumen/kategori/nxo92w0Eem',
          iconUrl: '/icon/ruang-gtk/pr-dokumen-rujukan-1.svg',
          tracker: { activityType: 'CLICK_DOKUMEN_PENINGKATAN_KOMPETENSI' },
        },
      ],
    },
    {
      title: 'Lainnya',
      items: [
        {
          title: 'Pusat Bantuan',
          href:
            process.env.NEXT_PUBLIC_PUSAT_INFORMASI_RUMDIK_BASE_URL +
            '/hc/id/categories/44586577246105-Ruang-GTK',
          iconUrl: '/icon/ruang-gtk/pr-pusat-bantuan.svg',
        },
        {
          title: 'Persyaratan Layanan',
          href: '/tns',
          iconUrl: '/icon/ruang-gtk/pr-persyaratan-layanan.svg',
        },
        {
          title: 'Kebijakan Privasi',
          href: '/policy',
          iconUrl: '/icon/ruang-gtk/pr-kebijakan-privasi.svg',
        },
      ],
    },
  ]

  return (
    <>
      <Head>
        <title>Beranda - Ruang GTK</title>
      </Head>
      <div className="relative pb-16 lg:pb-0">
        <Header />
        <div
          className={clsx(
            banners?.length
              ? 'bg-[#2A7EFB] hidden lg:block absolute h-[280px] left-0 right-0 top-0'
              : 'hidden'
          )}
        />
        <section
          className={clsx(
            styles['base-rgtk-homepage'],
            'mx-auto flex w-full lg:!pt-2',
            banners?.length ? 'bg-surface-subdued lg:bg-white' : 'hidden'
          )}
        >
          <Carousel banners={banners} />
        </section>
        <KSCard />
        <section className="bg-gray-0">
          <div
            className={clsx(
              styles['base-rgtk-homepage'],
              'mx-0 flex flex-col w-full',
              'space-y-4 lg:space-y-5'
            )}
          >
            <Text variant={isDesktop ? 'display-sm' : 'heading-md'}>
              Layanan yang Tersedia
            </Text>
            {MENU_PANEL_ITEMS?.map((menuPanel) => (
              <MenuPanel
                key={menuPanel.title}
                title={menuPanel.title}
                items={menuPanel.items}
              />
            ))}
          </div>
        </section>
        <BottomNav />
      </div>
    </>
  )
}

export default Homepage
